import { useState } from "react";
import { useLocation } from "react-router-dom";
import SideNav from "./components/SideNav/sideNav";
import { Layout } from "antd";
import { RoutesList } from "./components/routes";
import { useAuth } from "./utils/hooks/useAuth";
import AppHeader from "./components/AdvisoryHeader/AdvisoryHeader";
import { ScrollToTop } from "./components/ScrollToTop";
import { isGenericPartner } from "./config/genericPartners";

// Standalone form routes that should bypass all app logic
const STANDALONE_FORM_ROUTES = [
  "/verify-phone",
  "/select-address",
  "/sample-request-form",
  "/form-unavailable",
  "/sample-order-form",
];

// Subpaths under /:partner/* that should also bypass the app shell, matching
// the legacy Medvantx flat routes above. Keeps width/layout parity between
// /sample-request-form and /:partner/sample-request-form.
const STANDALONE_PARTNER_FORM_SUBPATHS = new Set([
  "verify-phone",
  "select-address",
  "sample-request-form",
  "form-unavailable",
]);

function isStandalonePartnerFormPath(pathname: string): boolean {
  const segments = pathname.toLowerCase().split("/").filter(Boolean);
  if (segments.length !== 2) return false;
  return (
    isGenericPartner(segments[0]) &&
    STANDALONE_PARTNER_FORM_SUBPATHS.has(segments[1])
  );
}

const App = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [background, setBackgroundImage] = useState<any>({
    background: "white",
  });

  const pathname = location.pathname.toLowerCase();
  const isStandaloneForm =
    STANDALONE_FORM_ROUTES.includes(pathname) ||
    isStandalonePartnerFormPath(pathname);

  // For standalone forms, render without any app layout or auth checks
  if (isStandaloneForm) {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          background: "transparent",
        }}
      >
        <RoutesList setBackgroundImage={setBackgroundImage} />
      </Layout>
    );
  }

  // Regular app layout with auth checks
  return (
    <Layout
      style={{
        maxHeight: "100svh",
        ...(!isAuthenticated && { overflowY: "hidden" }),
      }}
    >
      {isAuthenticated &&
        window.location.href.split("/").pop() !== "verify-otp" && <AppHeader />}
      <Layout
        className="inner-layout custom-scroller"
        style={{
          padding: "0 0",
          overflowX: "hidden",
          height: !isAuthenticated ? "calc(100svh - 64px)" : "100svh",
          flexDirection: "row",
          alignItems: "baseline",
          ...background,
        }}
      >
        <ScrollToTop />
        {isAuthenticated &&
          window.location.href.split("/").pop() !== "verify-otp" && <SideNav />}
        <RoutesList setBackgroundImage={setBackgroundImage} />
      </Layout>
    </Layout>
  );
};

export default App;
